import { Suspense, lazy, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SrIcon } from "@/components/ui/SrIcon";
import { MapsProvider } from "./MapsProvider";
import { ProjectLocationStatusBadge } from "./ProjectLocationStatusBadge";
import { useLocationsOverview, useSignedImageUrls } from "@/hooks/use-locations";
import { useProjectLocations, useRemoveProjectLocation, useUpdateProjectLocation } from "@/hooks/use-project-locations";
import { hasGoogleMaps } from "@/lib/locations/maps-config";
import { locationSubtitle } from "@/lib/locations/filter";
import {
  PROJECT_LOCATION_STATUSES,
  PROJECT_LOCATION_STATUS_LABEL,
  compareProjectLocationStatus,
} from "@/lib/locations/project-status";
import type { DBProjectLocation, ProjectLocationStatus } from "@/lib/db-types";

const LocationMap = lazy(() => import("./LocationMap"));

/**
 * Fanen «Lokasjoner» på prosjektkortet (VIB-2940, mockup I): tabell med
 * statusvelger og kart. Endre og fjerne: den som la til, PL, rådgiver og
 * admin (RLS). Avsluttede prosjekter er låst.
 */
export function ProjectLocationsTab({
  projectId,
  locked,
  canManage,
}: {
  projectId: string;
  locked: boolean;
  canManage: (link: DBProjectLocation) => boolean;
}) {
  const { data: links = [], isLoading } = useProjectLocations(projectId);
  const { data: all = [] } = useLocationsOverview(links.length > 0);
  const update = useUpdateProjectLocation();
  const remove = useRemoveProjectLocation();

  const byId = useMemo(() => new Map(all.map((l) => [l.id, l])), [all]);
  const rows = useMemo(
    () =>
      links
        .map((link) => ({ link, location: byId.get(link.location_id) }))
        .sort((a, b) => compareProjectLocationStatus(a.link.status, b.link.status) || (a.location?.name ?? "").localeCompare(b.location?.name ?? "", "nb")),
    [links, byId],
  );
  const { data: coverUrls = {} } = useSignedImageUrls(rows.map((r) => r.location?.cover_path ?? ""));
  const mapRows = rows.flatMap((r) => (r.location ? [r.location] : []));

  const onStatus = (link: DBProjectLocation, status: ProjectLocationStatus) =>
    update.mutate(
      { link, patch: { status } },
      { onSuccess: () => toast.success(`Satt til ${PROJECT_LOCATION_STATUS_LABEL[status]}`), onError: (e) => toast.error((e as Error).message) },
    );
  const onRemove = (link: DBProjectLocation) =>
    remove.mutate(link, { onSuccess: () => toast.success("Lokasjonen er fjernet fra prosjektet"), onError: (e) => toast.error((e as Error).message) });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0 pb-3">
          <CardTitle className="text-lg">
            Lokasjoner <span className="font-body text-sm font-normal text-muted-foreground">{links.length}</span>
          </CardTitle>
          <Button size="sm" variant="outline" asChild>
            <Link to="/lokasjoner">
              <SrIcon name="search" className="h-4 w-4" />
              Finn lokasjoner
            </Link>
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {locked && <p className="text-sm text-muted-foreground">Prosjektet er avsluttet, så statusene kan ikke endres.</p>}
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Laster …</p>
          ) : rows.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Ingen lokasjoner ennå. Velg «Legg på prosjekt» på en lokasjon, eller velg flere i tabellen under Lokasjoner.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Lokasjon</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-12"><span className="sr-only">Valg</span></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(({ link, location }) => {
                  const cover = location?.cover_path ? coverUrls[location.cover_path] : undefined;
                  const editable = !locked && canManage(link);
                  return (
                    <TableRow key={link.id}>
                      <TableCell>
                        <span className="flex items-center gap-3">
                          {cover ? <img src={cover} alt="" className="h-10 w-14 shrink-0 object-cover" /> : <span className="h-10 w-14 shrink-0 bg-muted" aria-hidden />}
                          <span className="min-w-0">
                            {location ? (
                              <Link to={`/lokasjoner/${location.slug}`} className="block font-medium hover:underline">{location.name}</Link>
                            ) : (
                              <span className="block text-muted-foreground">Laster …</span>
                            )}
                            {location && <span className="block truncate text-xs text-muted-foreground">{locationSubtitle(location)}</span>}
                          </span>
                        </span>
                      </TableCell>
                      <TableCell>
                        {editable ? (
                          <Select value={link.status} onValueChange={(v) => onStatus(link, v as ProjectLocationStatus)} disabled={update.isPending}>
                            <SelectTrigger className="h-auto w-auto gap-2 border-0 bg-transparent p-0 shadow-none" aria-label={`Status for ${location?.name ?? "lokasjonen"}`}>
                              <ProjectLocationStatusBadge status={link.status} />
                            </SelectTrigger>
                            <SelectContent>
                              {PROJECT_LOCATION_STATUSES.map((s) => (
                                <SelectItem key={s} value={s}>{PROJECT_LOCATION_STATUS_LABEL[s]}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <ProjectLocationStatusBadge status={link.status} />
                        )}
                      </TableCell>
                      <TableCell>
                        {editable && (
                          <Button
                            size="icon"
                            variant="ghost"
                            aria-label={`Fjern ${location?.name ?? "lokasjonen"} fra prosjektet`}
                            disabled={remove.isPending}
                            onClick={() => onRemove(link)}
                          >
                            <SrIcon name="trash" className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      {mapRows.some((l) => l.lat != null) && hasGoogleMaps() && (
        <MapsProvider>
          <Suspense fallback={<div className="h-[360px] rounded-md bg-muted" />}>
            <LocationMap rows={mapRows} onBoundsChange={() => undefined} />
          </Suspense>
        </MapsProvider>
      )}
    </div>
  );
}
